import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Briefcase,
  CreditCard,
  FileText,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface ServiceCardsProps {
  title?: string;
  description?: string;
  services?: Array<{
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    price: string;
    ctaText: string;
    color?: string;
  }>;
}

const ServiceCardsSection = ({
  title = "Our Services",
  description = "Comprehensive business solutions tailored for the UAE market",
  services = [
    {
      id: "1",
      title: "Formations & Banking",
      description:
        "Complete UAE mainland and free zone company formation with banking solutions",
      icon: <Briefcase className="h-6 w-6" />,
      price: "Starting from AED 12,000",
      ctaText: "Get Started",
      color: "bg-lavender-300",
    },
    {
      id: "2",
      title: "PRO and Visa",
      description:
        "Comprehensive government relations, document processing and visa services",
      icon: <Users className="h-6 w-6" />,
      price: "Starting from AED 3,000",
      ctaText: "Explore Options",
      color: "bg-peach-300",
    },
    {
      id: "3",
      title: "Accounting & Taxes",
      description:
        "Professional accounting, bookkeeping, VAT and Corporate tax services",
      icon: <FileText className="h-6 w-6" />,
      price: "Starting from AED 1,500/month",
      ctaText: "Learn More",
      color: "bg-skyblue-300",
    },
  ],
}: ServiceCardsProps) => {
  return (
    <section className="py-20 px-4 md:px-8 lg:px-12 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="p-6">
                <div
                  className={`${service.color || "bg-blue-50"} w-12 h-12 rounded-full flex items-center justify-center mb-4`}
                >
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <div className="text-primary font-medium mb-4">
                  {service.price}
                </div>
                <Button
                  className="w-full bg-coral-600 hover:bg-coral-700 text-white shadow-sm"
                  onClick={() => {
                    const serviceRoutes = {
                      "1": "/services/business-setup",
                      "2": "/services/pro-and-visa",
                      "3": "/services/accounting-and-taxes",
                    };
                    window.location.href =
                      serviceRoutes[service.id] ||
                      `/services/${service.id.toLowerCase()}`;
                  }}
                >
                  {service.ctaText}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceCardsSection;
